import { useEffect, useState } from "react";
import { ExternalLink, Map as MapIcon, Building2, Compass, CarFront, ConciergeBell, Navigation, FileText, MousePointerClick, Image as ImageIcon, Globe, Plus, Accessibility, PlaneTakeoff } from "lucide-react";
import api from "../lib/api";

/* Airport Directions & Maps — the Airport Navigation Center (owner spec §27).
   Verified official map links grouped by purpose, Google directions, official
   site fallback and a traveler "suggest a map" queue. */

const GROUP_ICONS = {
  interactive: <MousePointerClick className="w-5 h-5" />,
  terminal: <Building2 className="w-5 h-5" />,
  navigation: <Compass className="w-5 h-5" />,
  transportation: <CarFront className="w-5 h-5" />,
  services: <ConciergeBell className="w-5 h-5" />,
};
const GROUP_COLORS = {
  interactive: "#7c3aed", terminal: "#2563eb", navigation: "#d97706",
  transportation: "#dc2626", services: "#0d9488",
};
const FORMAT_LABEL = { pdf: "PDF", interactive: "Interactive", web: "Web", image: "Image" };
const FORMAT_ICON = {
  pdf: <FileText className="w-3 h-3" />, interactive: <MousePointerClick className="w-3 h-3" />,
  web: <Globe className="w-3 h-3" />, image: <ImageIcon className="w-3 h-3" />,
};

function MapRow({ m }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-zinc-100 last:border-b-0"
      data-testid={`airport-map-${m.map_type}`}>
      <div className="min-w-0">
        <div className="text-sm font-600 text-zinc-900 truncate">{m.title}</div>
        <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
          <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 ${m.official ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-zinc-300 bg-zinc-50 text-zinc-600"}`}>
            {m.official ? "Official" : "Third-Party"}
          </span>
          <span className="inline-flex items-center gap-1 border border-zinc-200 px-1.5 py-0.5 uppercase">
            {FORMAT_ICON[m.format] || FORMAT_ICON.web}{FORMAT_LABEL[m.format] || "Web"}
          </span>
        </div>
      </div>
      <a href={m.map_url} target="_blank" rel="noopener noreferrer" data-testid={`view-map-${m.map_type}`}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-700 text-white bg-blue-600 hover:bg-blue-700 transition-colors">
        VIEW MAP <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function SuggestForm({ iata, onDone }) {
  const [form, setForm] = useState({ map_name: "", map_url: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const r = await api.post(`/airports/${iata}/maps/suggest`, form);
      setMsg({ ok: true, text: r.data.message });
      setTimeout(onDone, 2500);
    } catch (err) {
      setMsg({ ok: false, text: err.response?.status === 401 ? "Please sign in to suggest a map." : (err.response?.data?.detail || "Could not submit — check the link and try again.") });
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-zinc-300 bg-zinc-50 p-4 space-y-2.5" data-testid="suggest-map-form">
      <div className="text-sm font-700">Suggest a missing map</div>
      <input required value={form.map_name} onChange={(e) => setForm({ ...form, map_name: e.target.value })}
        placeholder="Map name (e.g. Terminal 2 Parking Map)" data-testid="suggest-map-name"
        className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
      <input required type="url" value={form.map_url} onChange={(e) => setForm({ ...form, map_url: e.target.value })}
        placeholder="https:// link to the official map" data-testid="suggest-map-url"
        className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
      <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
        placeholder="Notes (optional)" className="w-full border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy} data-testid="suggest-map-submit"
          className="px-4 py-2 text-sm font-700 text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60">
          {busy ? "Submitting…" : "Submit for review"}
        </button>
        {msg && <span className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="suggest-map-msg">{msg.text}</span>}
      </div>
    </form>
  );
}

function TerminalNavigator({ iata }) {
  const [airlines, setAirlines] = useState(null);
  const [picked, setPicked] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => {
    setAirlines(null); setPicked(""); setResult(null);
    api.get(`/airports/${iata}/terminal-navigator`).then((r) => setAirlines(r.data.airlines || [])).catch(() => setAirlines([]));
  }, [iata]);
  useEffect(() => {
    if (!picked) { setResult(null); return; }
    api.get(`/airports/${iata}/terminal-navigator`, { params: { airline: picked } })
      .then((r) => setResult(r.data)).catch(() => setResult(null));
  }, [picked, iata]);

  if (!airlines || airlines.length === 0) return null;
  return (
    <div className="border border-blue-200 bg-blue-50/60 p-5" data-testid="terminal-navigator">
      <div className="flex items-center gap-2 font-heading font-900 text-lg mb-1">
        <PlaneTakeoff className="w-5 h-5 text-blue-700" /> Terminal Navigator
      </div>
      <p className="text-sm text-zinc-700 mb-3">Flying from here? Pick your airline to see which terminal to head to — with the right map.</p>
      <select value={picked} onChange={(e) => setPicked(e.target.value)} data-testid="terminal-navigator-select"
        className="w-full max-w-sm border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-blue-600">
        <option value="">Select your airline…</option>
        {airlines.map((a) => (
          <option key={`${a.airline_iata}-${a.airline_name}`} value={a.airline_iata || a.airline_name}>
            {a.airline_name}{a.airline_iata ? ` (${a.airline_iata})` : ""}
          </option>
        ))}
      </select>
      {result?.found && (
        <div className="mt-4" data-testid="terminal-navigator-result">
          <div className="text-sm">
            <span className="font-700">{result.entry.airline_name}</span> checks in at{" "}
            <span className="inline-block bg-blue-700 text-white font-900 px-2 py-0.5">{result.entry.terminal}</span>
            {result.entry.notes && <span className="text-muted-foreground"> — {result.entry.notes}</span>}
          </div>
          {result.maps?.length > 0 && (
            <div className="mt-3 border border-zinc-300 bg-white">
              {result.maps.map((m) => <MapRow key={m.map_url} m={m} />)}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">Terminal assignments can change — always verify on your ticket or the airline's website before heading to the airport.</p>
        </div>
      )}
    </div>
  );
}

export default function AirportMaps({ iata, airportName }) {
  const [data, setData] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  useEffect(() => {
    setData(null);
    api.get(`/airports/${iata}/maps`).then((r) => setData(r.data)).catch(() => setData({ groups: [], count: 0 }));
  }, [iata]);

  if (!data) return <div className="py-10 text-center text-muted-foreground text-sm">Loading maps…</div>;

  return (
    <div className="space-y-6" data-testid="airport-maps-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading font-900 text-2xl sm:text-3xl flex items-center gap-2">
          <MapIcon className="w-7 h-7 text-blue-600" /> Airport Directions &amp; Maps
        </h2>
        <button onClick={() => setShowSuggest((s) => !s)} data-testid="suggest-map-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-700 border border-zinc-300 bg-white hover:border-blue-500 transition-colors">
          <Plus className="w-4 h-4" /> Suggest a map
        </button>
      </div>
      <p className="text-sm text-zinc-700 -mt-2">
        Know the airport before you arrive — terminal layouts, parking, ground transportation and services at {airportName}, linked directly to official sources.
      </p>

      {showSuggest && <SuggestForm iata={iata} onDone={() => setShowSuggest(false)} />}

      <TerminalNavigator iata={iata} />

      {/* always-available directions */}
      <div className="grid sm:grid-cols-2 gap-3">
        {data.google_maps_url && (
          <a href={data.google_maps_url} target="_blank" rel="noopener noreferrer" data-testid="google-directions-link"
            className="flex items-center justify-between border border-zinc-300 bg-white p-4 hover:border-blue-500 transition-colors">
            <span className="flex items-center gap-2.5 text-sm font-700"><Navigation className="w-5 h-5 text-blue-600" /> Driving directions to the airport</span>
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">Google Maps <ExternalLink className="w-3 h-3" /></span>
          </a>
        )}
        {data.official_site && (
          <a href={data.official_site} target="_blank" rel="noopener noreferrer" data-testid="official-site-link"
            className="flex items-center justify-between border border-zinc-300 bg-white p-4 hover:border-blue-500 transition-colors">
            <span className="flex items-center gap-2.5 text-sm font-700"><Globe className="w-5 h-5 text-emerald-700" /> Official airport website</span>
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">Official <ExternalLink className="w-3 h-3" /></span>
          </a>
        )}
      </div>

      {data.count > 0 ? (
        <div className="grid lg:grid-cols-2 gap-5">
          {data.groups.map((g) => (
            <div key={g.key} className="border border-zinc-300 bg-white border-t-4" style={{ borderTopColor: GROUP_COLORS[g.key] }}
              data-testid={`map-group-${g.key}`}>
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
                <span style={{ color: GROUP_COLORS[g.key] }}>{GROUP_ICONS[g.key]}</span>
                <h3 className="font-heading font-700">{g.label}</h3>
                <span className="ml-auto text-xs text-muted-foreground">{g.items.length}</span>
              </div>
              {g.items.map((m) => <MapRow key={m.map_url} m={m} />)}
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-amber-300 bg-amber-50 p-5" data-testid="maps-fallback">
          <div className="flex items-center gap-2 font-700 text-amber-900 mb-1"><Accessibility className="w-4 h-4 hidden" /><MapIcon className="w-4 h-4" /> Maps research in progress</div>
          <p className="text-sm text-amber-900">
            We haven't verified individual maps for this airport yet.{" "}
            {data.official_site
              ? <>General information &amp; maps are available on the <a href={data.official_site} target="_blank" rel="noopener noreferrer" className="font-700 underline">official airport website</a>.</>
              : <>Check the airport's official website for terminal and parking maps.</>}{" "}
            Know a good official map? Use <button onClick={() => setShowSuggest(true)} className="font-700 underline">Suggest a map</button> to help fellow travelers.
          </p>
        </div>
      )}

      {data.count > 0 && (
        <p className="text-xs text-muted-foreground">
          Map coverage {data.coverage}% · {data.count} verified links · All maps belong to their respective airport authorities.
          Missing something? <button onClick={() => setShowSuggest(true)} className="underline font-600">Suggest a map</button>.
        </p>
      )}
    </div>
  );
}
